"use client";
import { Image, Link } from "@heroui/react";
import { Menu } from "lucide-react";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { useLocation } from "wouter";
import { useAppApi } from "src/appApi";
import { useStore } from "src/store/store";

const Header = () => {
	// Phone-width menu, open or closed. The whole button toggles it (it used to
	// listen on the 24px icon only, so most taps on the button did nothing).
	const [menuOpen, setMenuOpen] = useState(false);
	const [location] = useLocation();

	const store = useStore();

	const { user } = store;

	const appApi = useAppApi();

	// Check if we're on pharmacy page
	const isPharmacyPage = location.startsWith("/pharmacy");

	const toggleMenu = () => setMenuOpen((open) => !open);

	let special_links = null;

	function navigateToProfile() {
		if (user?.recommendingDoctor) {
			return "/doctor";
		} else if (user?.admin) {
			return "/admin";
		}
		if (user?.broker) {
			return "/broker";
		}
		return "/my_profile";
	}

	const linkToProfile = navigateToProfile();

	if (!store.user) {
		special_links = (
			<>
				<Link
					href="/login"
					className="rounded border border-transparent p-2 text-center text-indigo-600 transition-colors duration-300 hover:bg-indigo-100 hover:text-indigo-700 md:mx-2 lg:px-4"
				>
					התחברות
				</Link>
				<Link
					href="/register"
					className="mt-1 rounded border border-solid border-indigo-600 p-2 text-center text-indigo-600 transition-colors duration-300 hover:bg-indigo-600 hover:text-white md:mx-2 md:ml-1 md:mt-0 lg:px-4"
				>
					הרשמה
				</Link>
			</>
		);
	} else {
		special_links = (
			<>
				<Link
					href={linkToProfile}
					className="rounded border border-transparent p-2 text-center text-indigo-600 transition-colors duration-300 hover:bg-indigo-100 hover:text-indigo-700 md:mx-2 lg:px-4"
				>
					הפרופיל שלי
				</Link>
				<Link
					onPress={appApi.api.system.logout}
					className="mt-1 rounded border border-solid border-indigo-600 p-2 text-center text-indigo-600 transition-colors duration-300 hover:bg-indigo-600 hover:text-white md:mx-2 md:ml-1 md:mt-0 lg:px-4"
				>
					התנתקות
				</Link>
			</>
		);
	}

	return (
		<nav className="top-0 z-10 border-b border-b-gray-400 py-2 sm:relative">
			<div className="container mx-auto px-4 md:flex md:items-center">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Image src={"/logo.png"} width={50} height={50} alt={"CANA Logo"} />

						<Link href={"/"} className="ml-2 text-xl font-bold text-indigo-600">
							CANA
						</Link>

						{/* PHARMX Logo - Only on pharmacy pages */}
						{isPharmacyPage && (
							<>
								<div className="h-8 w-px bg-gray-300"></div>
								<div className="flex items-center gap-2">
									<div className="bg-gray-100 rounded p-1.5 flex-shrink-0">
										<Image
											src="/pharmacy-logo.jpeg"
											alt="PHARMX Logo"
											className="max-w-[40px] sm:max-w-[50px] h-auto"
											removeWrapper
										/>
									</div>
									<span className="text-xs text-gray-600 hidden sm:inline whitespace-nowrap">
										בשיתוף פעולה עם <span className="font-bold text-indigo-600">PHARMX</span>
									</span>
								</div>
							</>
						)}
					</div>
					<button
						type="button"
						onClick={toggleMenu}
						aria-label="תפריט"
						aria-expanded={menuOpen}
						aria-controls="main-menu"
						className="rounded border border-solid border-gray-600 px-3 py-2 text-gray-600 opacity-50 hover:opacity-75 md:hidden"
					>
						<Menu aria-hidden="true" />
					</button>
				</div>

				<div
					id="main-menu"
					className={`mt-3 ${menuOpen ? "flex" : "hidden"} flex-col md:ml-auto md:mr-20 md:mt-0 md:flex md:flex-row`}
					onClick={() => setMenuOpen(false)}
				>
					<a
						href="/about_us"
						className="rounded p-2 text-accent-foreground transition-colors duration-300 hover:bg-accent md:mx-2 lg:px-4"
					>
						אודות
					</a>
					<a
						href="/contact_us"
						className="rounded p-2 text-accent-foreground transition-colors duration-300 hover:bg-accent md:mx-2 lg:px-4"
					>
						יצירת קשר
					</a>
					{user && (
						<a
							href="/pharmacy"
							className="rounded p-2 text-accent-foreground transition-colors duration-300 hover:bg-accent md:mx-2 lg:px-4"
						>
							בית מרקחת
						</a>
					)}
					{user?.recommendingDoctor && (
						<>
							<a
								href="/doctor/templates"
								className="flex items-center gap-1 rounded p-2 text-accent-foreground transition-colors duration-300 hover:bg-accent md:mx-2 lg:px-4"
							>
								<Icon icon="lucide:file-text" />
								תבניות מסמך
							</a>
							<a
								href="/doctor/profile"
								className="flex items-center gap-1 rounded p-2 text-accent-foreground transition-colors duration-300 hover:bg-accent md:mx-2 lg:px-4"
							>
								<Icon icon="lucide:user" />
								פרופיל
							</a>
						</>
					)}
					{special_links}
				</div>
			</div>
		</nav>
	);
};

export default Header;
